import React, { useState, useEffect } from 'react';
import * as Routes from '@/modules/routes';
import Axios from 'axios';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';

type IntegrationStatusTypes = {
  lmsType: 'Canvas' | 'Google Classroom' | 'Canvas LTI 1.3' | 'Schoology LTI 1.3';
  programId: number;
};

const IntegrationStatus = ({
  lmsType, programId,
}: IntegrationStatusTypes) => {
  const [classrooms, setClassrooms] = useState([]);
  const [studentSeats, setStudentSeats] = useState([]);
  const [studentLmsIds, setStudentLmsIds] = useState([]);

  const LMS_MAP = {
    Canvas: 'canvas',
    'Canvas LTI 1.3': 'canvas_lti',
    'Google Classroom': 'google',
    'Schoology LTI 1.3': 'schoology',
  };

  const filterLmsIds = (lmsIds, seats) => (
    lmsIds.filter(id => id.user_type === 'Student' && seats.map(seat => seat.student_id).includes(id.user_id))
  );

  const isLti = lmsType.includes('LTI');

  useEffect(() => {
    const route = Routes.plato_api_me_path({ extra_attributes: [
      'classrooms', 'shared_student_seats', 'student_user_google_ids', 'student_user_canvas_ids',
      'student_canvas_lti_ids', 'student_schoology_lti_ids',
    ] });

    Axios.get(route)
      .then((res) => {
        const filteredClassrooms = res.data.staffer.classrooms.filter(classroom => classroom.program_id === programId);
        setClassrooms(filteredClassrooms);

        const filteredStudentSeats = res.data.staffer.shared_student_seats
          .filter(seat => filteredClassrooms.map(classroom => classroom.id).includes(seat.classroom_id));
        setStudentSeats(filteredStudentSeats);

        const lmsIdMapping = {
          Canvas: res.data.staffer.student_user_canvas_ids,
          'Google Classroom': res.data.staffer.student_user_google_ids,
          'Canvas LTI 1.3': res.data.staffer.student_canvas_lti_ids,
          'Schoology LTI 1.3': res.data.staffer.student_schoology_lti_ids,
        };

        const filteredLmsIds = filterLmsIds(lmsIdMapping[lmsType], filteredStudentSeats);
        setStudentLmsIds(filteredLmsIds);
      });
  }, [lmsType]);

  const classroomsForLms = () => classrooms.filter(classroom => (
    classroom.lms_type === LMS_MAP[lmsType] && !!classroom.lms_course_id
  ));

  const renderIcon = (syncedCount, totalCount) => {
    const iconName = syncedCount === totalCount ? 'CheckmarkCircle' : 'WarningOutlined';
    const iconFill = iconName === 'CheckmarkCircle' ? '#78A22F' : '#F69C05';

    return (
      <Icon fill={iconFill} icon={iconName} size={16} />
    );
  };

  const renderCard = (title, connectedLength, totalLength) => (
    <div className="tw-flex">
      <div className="tw-w-[1.5rem]">
        {renderIcon(connectedLength, totalLength)}
      </div>

      <p className="tw-mr-2">
        {title}
      </p>

      <p>
        {connectedLength}/{totalLength} Connected
      </p>

    </div>
  );

  return (
    <div className={cn({ 'tw-mt-1': isLti, 'tw-mt-5 tw-basis-1/2 tw-pl-3': !isLti })}>
      <Text className="tw-pb-1" size="sm" variant="label">
        Integration Status
      </Text>
      {!isLti && renderCard('Classrooms', classroomsForLms().length, classrooms.length)}
      {renderCard('Students', studentLmsIds.length, studentSeats.length)}
    </div>
  );
};

export default IntegrationStatus;
